import React, { useState } from 'react';
import { Plus, Edit, Trash2, ArrowRight, Loader2, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useTransformationRules } from '@/hooks/useTransformationRules';
import { useIngredients } from '@/pages/inventory/hooks/useIngredients';
import type { TransformationRule, TransformationSourceItem, TransformationTargetItem } from '@/types/transformation.types';

interface TransformationRuleFormData {
  name: string;
  description: string;
  sourceItems: TransformationSourceItem[];
  targetItems: TransformationTargetItem[];
}

export const TransformationRulesTab: React.FC = () => {
  const {
    rules,
    isLoading,
    error,
    createRule,
    updateRule,
    deleteRule
  } = useTransformationRules();

  const { allIngredients: availableIngredients, loading: ingredientsLoading } = useIngredients();

  const [selectedRule, setSelectedRule] = useState<TransformationRule | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [formData, setFormData] = useState<TransformationRuleFormData>({
    name: '',
    description: '',
    sourceItems: [],
    targetItems: []
  });

  const handleOpenModal = (rule?: TransformationRule) => {
    if (rule) {
      setSelectedRule(rule);
      setFormData({
        name: rule.name,
        description: rule.description || '',
        sourceItems: rule.sourceItems || [],
        targetItems: rule.targetItems || []
      });
    } else {
      setSelectedRule(null);
      setFormData({
        name: '',
        description: '',
        sourceItems: [],
        targetItems: []
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRule(null);
  };

  const handleInputChange = (field: keyof TransformationRuleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Source items management
  const addSourceItem = () => {
    if (availableIngredients.length === 0) return;

    const newItem: TransformationSourceItem = {
      ingredientId: '',
      quantity: 1,
      unit: 'kg'
    };

    setFormData(prev => ({
      ...prev,
      sourceItems: [...prev.sourceItems, newItem]
    }));
  };

  const updateSourceItem = (index: number, field: keyof TransformationSourceItem, value: any) => {
    setFormData(prev => {
      const updated = [...prev.sourceItems];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-update unit when ingredient changes
      if (field === 'ingredientId') {
        const ingredient = availableIngredients.find(ing => ing.id.toString() === value.toString());
        if (ingredient) {
          updated[index].unit = ingredient.unit;
        }
      }

      return { ...prev, sourceItems: updated };
    });
  };

  const removeSourceItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      sourceItems: prev.sourceItems.filter((_, i) => i !== index)
    }));
  };

  // Target items management - ONLY ONE target allowed
  const updateTargetItem = (field: keyof TransformationTargetItem, value: any) => {
    setFormData(prev => {
      // Ensure we always have exactly one target item
      const targetItem = prev.targetItems[0] || { ingredientId: '', quantity: 1, unit: 'kg' };
      const updated = { ...targetItem, [field]: value };

      // Auto-update unit when ingredient changes
      if (field === 'ingredientId') {
        const ingredient = availableIngredients.find(ing => ing.id.toString() === value.toString());
        if (ingredient) {
          updated.unit = ingredient.unit;
        }
      }

      return { ...prev, targetItems: [updated] };
    });
  };

  // Calculate global conversion factor
  // Función para convertir todas las unidades a kg (peso equivalente)
  const convertToKg = (quantity: number, unit: string, ingredientName?: string): number => {
    const unitLower = unit.toLowerCase();

    // Unidades de peso directo
    if (unitLower === 'kg') {
      return quantity;
    }
    if (unitLower === 'gr') {
      return quantity / 1000;
    }

    // Unidades de volumen -> kg usando densidades aproximadas
    if (unitLower === 'lt') {
      // Para líquidos, usar densidad aproximada
      // Aceite vegetal: ~0.92 kg/lt
      // Agua/líquidos acuosos: ~1.0 kg/lt
      if (ingredientName?.toLowerCase().includes('aceite')) {
        return quantity * 0.92;
      }
      return quantity * 1.0; // Densidad del agua por defecto
    }
    if (unitLower === 'ml') {
      if (ingredientName?.toLowerCase().includes('aceite')) {
        return (quantity / 1000) * 0.92;
      }
      return (quantity / 1000) * 1.0;
    }

    // Unidades de conteo -> kg usando pesos aproximados
    if (unitLower === 'unidad') {
      // Pesos aproximados por unidad
      const nameLower = ingredientName?.toLowerCase() || '';

      if (nameLower.includes('huevo')) {
        return quantity * 0.06; // 1 huevo ≈ 60g
      }
      if (nameLower.includes('limón') || nameLower.includes('limon')) {
        return quantity * 0.05; // 1 limón ≈ 50g
      }
      if (nameLower.includes('tomate')) {
        return quantity * 0.15; // 1 tomate ≈ 150g
      }
      if (nameLower.includes('cebolla')) {
        return quantity * 0.20; // 1 cebolla ≈ 200g
      }
      if (nameLower.includes('papa')) {
        return quantity * 0.18; // 1 papa ≈ 180g
      }
      if (nameLower.includes('pan')) {
        return quantity * 0.05; // 1 pan ≈ 50g
      }

      // Peso genérico para unidades no identificadas
      return quantity * 0.10; // 100g por unidad genérica
    }

    // Para otras unidades, retornar la cantidad directamente (kg equivalente genérico)
    return quantity * 0.10;
  };

  const calculateConversionFactor = (): number => {
    if (formData.sourceItems.length === 0 || formData.targetItems.length === 0) {
      return 0;
    }

    const targetQuantity = formData.targetItems[0]?.quantity || 0;
    if (targetQuantity <= 0) {
      return 0;
    }

    // Obtener las unidades de todos los insumos origen
    const sourceUnits = formData.sourceItems.map(item => {
      const ingredient = availableIngredients.find(ing => ing.id.toString() === item.ingredientId?.toString());
      return ingredient?.unit;
    }).filter(Boolean);

    // Verificar si hay mezcla de unidades diferentes
    const hasMixedUnits = new Set(sourceUnits).size > 1;

    if (hasMixedUnits) {
      // CASO MEZCLA: Convertir todo a kg para calcular correctamente
      const targetIngredientId = formData.targetItems[0]?.ingredientId;
      const targetIngredient = availableIngredients.find(ing => ing.id.toString() === targetIngredientId?.toString());

      if (!targetIngredient) return 0;

      const targetKg = convertToKg(targetQuantity, targetIngredient.unit, targetIngredient.name);
      let totalSourceKg = 0;

      for (const sourceItem of formData.sourceItems) {
        const sourceIngredient = availableIngredients.find(ing => ing.id.toString() === sourceItem.ingredientId?.toString());
        if (!sourceIngredient) continue;

        const sourceKg = convertToKg(sourceItem.quantity || 0, sourceIngredient.unit, sourceIngredient.name);
        totalSourceKg += sourceKg;
      }

      if (totalSourceKg <= 0) return 0;
      return parseFloat((targetKg / totalSourceKg).toFixed(4));
    } else {
      // CASO SIMPLE: Todas las unidades son iguales, cálculo directo
      const totalSourceQuantity = formData.sourceItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      if (totalSourceQuantity <= 0) return 0;
      return parseFloat((targetQuantity / totalSourceQuantity).toFixed(4));
    }
  };

  // Función para obtener el texto descriptivo de la conversión
  const getConversionDescription = (): string => {
    if (formData.sourceItems.length === 0 || formData.targetItems.length === 0) {
      return '';
    }

    const targetIngredientId = formData.targetItems[0]?.ingredientId;
    const targetIngredient = availableIngredients.find(ing => ing.id.toString() === targetIngredientId?.toString());
    const targetQuantity = formData.targetItems[0]?.quantity || 0;

    if (!targetIngredient || targetQuantity <= 0) {
      return '';
    }

    // Verificar si hay mezcla de unidades
    const sourceUnits = formData.sourceItems.map(item => {
      const ingredient = availableIngredients.find(ing => ing.id.toString() === item.ingredientId?.toString());
      return ingredient?.unit;
    }).filter(Boolean);

    const hasMixedUnits = new Set(sourceUnits).size > 1;

    if (hasMixedUnits) {
      // CASO MEZCLA: Mostrar peso equivalente en kg
      const targetKg = convertToKg(targetQuantity, targetIngredient.unit, targetIngredient.name);
      let totalSourceKg = 0;

      for (const sourceItem of formData.sourceItems) {
        const sourceIngredient = availableIngredients.find(ing => ing.id.toString() === sourceItem.ingredientId?.toString());
        if (!sourceIngredient) continue;

        const sourceKg = convertToKg(sourceItem.quantity || 0, sourceIngredient.unit, sourceIngredient.name);
        totalSourceKg += sourceKg;
      }

      return `${totalSourceKg.toFixed(2)} kg (equiv.) → ${targetKg.toFixed(2)} kg`;
    } else {
      // CASO SIMPLE: Mostrar unidades originales
      const totalSourceQuantity = formData.sourceItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const firstSourceIngredient = formData.sourceItems[0]?.ingredientId
        ? availableIngredients.find(ing => ing.id.toString() === formData.sourceItems[0].ingredientId?.toString())
        : null;

      const sourceUnit = firstSourceIngredient?.unit || 'unidad';
      const targetUnit = targetIngredient.unit;

      return `${totalSourceQuantity.toFixed(2)} ${sourceUnit} → ${targetQuantity.toFixed(2)} ${targetUnit}`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setShowValidationModal(true);
      return;
    }

    if (formData.sourceItems.length === 0 || formData.targetItems.length === 0) {
      setShowValidationModal(true);
      return;
    }

    // Validate all items have ingredient selected
    const hasEmptySource = formData.sourceItems.some(item => !item.ingredientId);
    const hasEmptyTarget = formData.targetItems.some(item => !item.ingredientId);

    if (hasEmptySource || hasEmptyTarget) {
      setShowValidationModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Calculate global conversion factor
      const conversionFactor = calculateConversionFactor();

      const ruleData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        sourceItems: formData.sourceItems,
        targetItems: formData.targetItems,
        conversionFactor: conversionFactor, // Global factor: targetQty / sum(sourceQty)
        isActive: true
      };

      if (selectedRule) {
        await updateRule(selectedRule.id, ruleData);
      } else {
        await createRule(ruleData);
      }

      handleCloseModal();
    } catch (error) {
      console.error('Error saving transformation rule:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRule = async (ruleId: string, ruleName: string) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar la regla "${ruleName}"?`)) {
      try {
        await deleteRule(ruleId);
      } catch (error) {
        console.error('Error deleting transformation rule:', error);
      }
    }
  };

  const getIngredientName = (ingredientId: string) => {
    if (!availableIngredients || availableIngredients.length === 0) {
      return 'Cargando...';
    }

    const ingredient = availableIngredients.find(ing =>
      ing.id.toString() === ingredientId.toString()
    );

    return ingredient?.name || 'No encontrado';
  };

  if (isLoading || ingredientsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-macartur-primary" />
        <span className="ml-2 text-gray-600">
          {ingredientsLoading ? 'Cargando ingredientes...' : 'Cargando reglas de transformación...'}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <Button
          onClick={() => window.location.reload()}
          variant="outline"
        >
          Reintentar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Reglas de Transformación</h2>
          <p className="text-gray-600 text-sm">
            Define cómo se transforman los insumos origen en insumos destino
          </p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-macartur-primary hover:bg-macartur-primary/90"
          disabled={isLoading}
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Regla
        </Button>
      </div>

      {/* Rules List */}
      <div className="grid gap-4">
        {rules.map((rule) => (
          <Card key={rule.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-3">
                    <h3 className="font-medium text-lg">{rule.name}</h3>
                  </div>

                  <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start">
                    {/* Source Items */}
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-500 uppercase">Insumos Origen</Label>
                      {rule.sourceItems && rule.sourceItems.length > 0 ? (
                        rule.sourceItems.map((sourceItem, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {getIngredientName(sourceItem.ingredientId)}
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {sourceItem.quantity} {sourceItem.unit}
                            </span>
                          </div>
                        ))
                      ) : (
                        // Fallback for legacy rules
                        rule.sourceIngredientId && (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {getIngredientName(rule.sourceIngredientId)}
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {rule.sourceQuantity} {rule.sourceUnit}
                            </span>
                          </div>
                        )
                      )}
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center justify-center pt-6">
                      <ArrowRight className="h-6 w-6 text-macartur-primary" />
                    </div>

                    {/* Target Items */}
                    <div className="space-y-2">
                      <Label className="text-xs text-gray-500 uppercase">Insumos Destino</Label>
                      {rule.targetItems && rule.targetItems.length > 0 ? (
                        rule.targetItems.map((targetItem, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              {getIngredientName(targetItem.ingredientId)}
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {targetItem.quantity} {targetItem.unit}
                            </span>
                          </div>
                        ))
                      ) : (
                        // Fallback for legacy rules
                        rule.targetIngredientId && (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-800 border-green-200">
                              {getIngredientName(rule.targetIngredientId)}
                            </Badge>
                            <span className="text-sm text-gray-600">
                              {rule.targetQuantity} {rule.targetUnit}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {rule.description && (
                    <p className="text-sm text-gray-500 mt-3">{rule.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenModal(rule)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                    onClick={() => handleDeleteRule(rule.id, rule.name)}
                    disabled={isLoading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {rules.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ArrowRight className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay reglas de transformación</h3>
            <p className="text-gray-600 text-center mb-4">
              Las reglas de transformación definen cómo convertir insumos origen en insumos destino.
            </p>
            <Button onClick={() => handleOpenModal()} className="bg-macartur-primary hover:bg-macartur-primary/90">
              <Plus className="h-4 w-4 mr-2" />
              Crear Primera Regla
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Rule Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedRule ? 'Editar Regla de Transformación' : 'Nueva Regla de Transformación'}
            </DialogTitle>
            <DialogDescription>
              Define los insumos origen que se transformarán en los insumos destino
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre de la Regla *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="ej: Jamón Picado para Rellenos"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descripción opcional del proceso de transformación..."
                  rows={2}
                />
              </div>
            </div>

            <div className="space-y-6">
              {/* Source Ingredients */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Insumos Origen *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSourceItem}
                    className="text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Agregar
                  </Button>
                </div>

                {formData.sourceItems.length === 0 && (
                  <div className="text-center py-6 bg-blue-50 rounded-lg border-2 border-dashed border-blue-200">
                    <p className="text-sm text-blue-600">
                      Agrega insumos origen
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  {/* Header - shown only once */}
                  {formData.sourceItems.length > 0 && (
                    <div className="flex items-center gap-2 px-3 pb-2">
                      <div className="flex-1">
                        <Label className="text-xs font-semibold text-gray-600">Insumo</Label>
                      </div>
                      <div className="w-32">
                        <Label className="text-xs font-semibold text-gray-600">Cant.</Label>
                      </div>
                      <div className="w-9"></div> {/* Space for delete button */}
                    </div>
                  )}

                  {/* Items list */}
                  <div className="max-h-[400px] overflow-y-auto pr-2 space-y-2">
                    {formData.sourceItems.map((sourceItem, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex-1">
                          <Select
                            value={sourceItem.ingredientId.toString()}
                            onValueChange={(value) => updateSourceItem(index, 'ingredientId', value)}
                          >
                            <SelectTrigger className="h-9 bg-white">
                              <SelectValue placeholder="Seleccionar" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {availableIngredients.map((ingredient) => (
                                <SelectItem key={ingredient.id} value={ingredient.id.toString()}>
                                  {ingredient.name} ({ingredient.unit})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="w-32">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={sourceItem.quantity}
                            onChange={(e) => updateSourceItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="h-9"
                          />
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSourceItem(index)}
                          className="h-9 w-9 p-0 text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Target Ingredient - ONLY ONE */}
              <div className="space-y-3">
                <Label className="text-base font-semibold">Insumo Destino *</Label>

                <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-xs">Insumo</Label>
                      <Select
                        value={formData.targetItems[0]?.ingredientId?.toString() || ''}
                        onValueChange={(value) => updateTargetItem('ingredientId', value)}
                      >
                        <SelectTrigger className="h-9 bg-white">
                          <SelectValue placeholder="Seleccionar insumo destino" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {availableIngredients.map((ingredient) => (
                            <SelectItem key={ingredient.id} value={ingredient.id.toString()}>
                              {ingredient.name} ({ingredient.unit})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="w-24">
                      <Label className="text-xs">Cantidad</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.targetItems[0]?.quantity || 1}
                        onChange={(e) => updateTargetItem('quantity', parseFloat(e.target.value) || 0)}
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Conversion Factor Display */}
              {formData.sourceItems.length > 0 && formData.targetItems[0]?.quantity && (
                <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Factor de Conversión (calculado automáticamente)
                  </h3>
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {calculateConversionFactor().toFixed(4)}x
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {getConversionDescription()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="bg-macartur-primary hover:bg-macartur-primary/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {selectedRule ? 'Actualizando...' : 'Creando...'}
                  </>
                ) : (
                  selectedRule ? 'Actualizar Regla' : 'Crear Regla'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Validation Modal */}
      <Dialog open={showValidationModal} onOpenChange={setShowValidationModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Campos Requeridos
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Por favor complete todos los campos requeridos:
            </p>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li>• Nombre de la regla</li>
              <li>• Al menos un ingrediente origen</li>
              <li>• Al menos un ingrediente destino</li>
              <li>• Todos los ingredientes deben estar seleccionados</li>
            </ul>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowValidationModal(false)}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
